import axios from "axios";
import { getCookie } from "@/services/util.js";
import { verifyErrorMessaging } from "@/constants";

const API_URL = process.env.VUE_APP_BASEURL;
const DIGITAL_URL = process.env.VUE_APP_DIGITAL_URL;
const GATEWAY_URL = process.env.VUE_APP_GATEWAY_URL;
// const API_PATH = '/api/';
const API = API_URL + "/";
const FIELD_API = API + "user/field";
const MARKETING_API = API + "user/marketing";
const SELLER_API = API + "seller-ext";
const CASH_OFFER_API = API + "cash-offer";
const VERIFICATION_URL = process.env.VUE_APP_VERIFICATION_URL + '/verification';
const VISITOR_IDENTIFIER = "uuid";
const TP_API = "https://api.trustpilot.com/v1/reviews/";
const TP_APIKEY = "wcfqZXcFzgQwjK6gEyiQ2vbYWDnzteOF";
const UPLOAD_API_URL = GATEWAY_URL + "/file/upload";
const UPLOAD_API_KEY = process.env.VUE_APP_UPLOAD_API_KEY;
const UPLOAD_BUCKET = process.env.VUE_APP_UPLOAD_BUCKET || "idealagent-cash-offer-client-files";

/**
 * Gets HTML page to execute on form conversion
 * @param source
 * @returns {Promise<null|AxiosResponse<T>>}
 */
async function getConversionHtmlPage(source) {
  try {
    let response = await axios.get(
      DIGITAL_URL + "/conversion-code?source=" + source
    );

    return response;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Gets whether we should validate phone number for this source
 * @param source
 * @returns {Promise<null|AxiosResponse<T>>}
 */
async function shouldValidatePhone(source) {
  try {
    let response = await axios.get(
      DIGITAL_URL + "/validate-phone?source=" + source
    );

    return response;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Gets agent information from the API based on a place
 * @param place
 * @returns {Promise<T>}
 */
async function getSuggestedAgent(place) {
  try {
    let response;

    response = await axios.post(API + "agent-preview/", place);

    return response.data;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Gets agent information from the API based on a place
 * @param place
 * @returns {Promise<T>}
 */
async function getTrustPilotReview(id) {
  try {
    let response;

    response = await axios.get(TP_API + id + "?apikey=" + TP_APIKEY);

    return response.data;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Saves field information for a visitor
 * @param field
 * @returns {Promise<T>}
 */
async function saveMarketing(name, value) {
  try {
    let vid = getCookie(VISITOR_IDENTIFIER);
    let field = {
      formId: "sign-up",
      name: name,
      value: value,
      vid: vid,
    };

    let response;

    response = await axios.put(MARKETING_API, field);

    return response.data;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Saves field information for a visitor
 * @param field
 * @returns {Promise<T>}
 */
async function saveField(name, value, validate) {
  try {
    let vid = getCookie(VISITOR_IDENTIFIER);
    let field = {
      formId: "sign-up-cash-offer",
      name: name,
      value: value,
      vid: vid,
      validate: validate,
    };

    let response;

    response = await axios.put(FIELD_API, field);

    return response.data;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Save full seller profile on completion
 * @param seller
 * @returns {Promise<T>}
 */
async function saveSeller(seller) {
  try {
    let vid = getCookie(VISITOR_IDENTIFIER);
    seller.vid = vid;
    let response;

    response = await axios.put(SELLER_API, seller);

    return response.data;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Save full seller profile on completion
 * @param seller
 * @returns {Promise<T>}
 */
async function saveCashOffer(seller) {
  try {
    let vid = getCookie(VISITOR_IDENTIFIER);
    seller.sellerData.vid = vid;
    let response;

    response = await axios.put(CASH_OFFER_API, seller);

    return response.data;
  } catch (error) {
    console.log(error);

    return null;
  }
}

/**
 * Validates entered verification code for this user
 * @param code
 * @param to
 * @returns {Promise<null|*>}
 */
async function validateVerificationCode(code, to){
    try {
        let response;

        response = await axios.post(VERIFICATION_URL, { code, to });

        return response.data;

    } catch (error) {
        return handleValidationErrorResponse(error);
    }
}

/**
 * Sends verification code for this user
 * @param type
 * @param to
 * @returns {Promise<null|*>}
 */
async function sendVerificationCode(type, to){
    try {
        let response;

        response = await axios.put(VERIFICATION_URL, { type, to });

        return response.data;

    } catch (error) {
        return handleValidationErrorResponse(error);
    }
}

const handleValidationErrorResponse = (error) => {
    // don't detour form submission if our validation engine is offline
    return error.response.status === 500 ? 
        { status: error.response.status, message: 'approved' } :
        { status: error.response.status, errorMessage: verifyErrorMessaging[error.response.status] }
}

async function uploadFile(file) {
  try {
    const response = await axios.get(
      `${UPLOAD_API_URL}?bucket=${UPLOAD_BUCKET}&file=${file.name}&type=${file.type}`,
      {
        headers: {
          "x-api-key": UPLOAD_API_KEY,
        },
      }
    );
    await axios.put(response.data.uploadURL, file, {
      headers: {
        "Content-Type": file.type,
      },
    });

    return {
      fileName: file.name,
      storageName: response.data.fileName,
    };
  } catch (error) {
    console.log(error);

    return null;
  }
}

export {
  getSuggestedAgent,
  getTrustPilotReview,
  saveField,
  saveSeller,
  saveMarketing,
  validateVerificationCode,
  sendVerificationCode,
  getConversionHtmlPage,
  shouldValidatePhone,
  uploadFile,
  saveCashOffer,
};
